import {
  Button,
  FormLabel,
  Grid,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import TicketSearch from "../../../../Components/TicketSearch";
import React, { useState, useEffect } from "react";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";

import { useTranslation } from "react-i18next";
import ListXrayResult from "./ListXrayResult";

const buttonStyle = {
  textTransform: "capitalize",
  backgroundColor: "#071952",
  fontWeight: "bold",
};

const TestXrayResult = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketSearchInput, setTicketSearchInput] = useState("");

  const ticketSearchQuery = ticketSearchInput.trim();
  const { data: ticketsResponse } = useFetchObjects(
    ticketSearchQuery ? ["tickets", "search", ticketSearchQuery] : null,
    ticketSearchQuery
      ? `tickets/?search=${encodeURIComponent(ticketSearchQuery)}`
      : "",
    token,
  );
  const ticketsOptions = Array.isArray(ticketsResponse)
    ? ticketsResponse
    : (ticketsResponse?.results ?? []);

  // Fetch X-rays only for the selected ticket (ticket-first flow)
  const { data: xrayData } = useFetchObjects(
    selectedTicket?.id != null ? ["patient-xray", "ticket", selectedTicket.id] : null,
    selectedTicket?.id != null
      ? `patient-xray/?ticket=${selectedTicket.id}`
      : "",
    token,
  );

  const { handleAdd, resetForm, loading } = useAdd(
    "patient-xray-results",
    token,
  );

  const [formData, setFormData] = useState({
    patient_xray: "",
    image: null,
    findings: "",
    result_date: new Date().toISOString().split("T")[0],
  });

  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);

  // Reset form when resetForm becomes true
  useEffect(() => {
    if (resetForm) {
      setFormData({
        patient_xray: "",
        image: null,
        findings: "",
        result_date: new Date().toISOString().split("T")[0],
      });
    }
  }, [resetForm]);

  // Clear selected X-ray when ticket changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      patient_xray: "",
      patient_name: "",
      xray_type: "",
    }));
  }, [selectedTicket?.id]);

  const handleInputChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handlePatientSelect = (event, selectedXray) => {
    if (selectedXray) {
      setFormData({
        ...formData,
        patient_xray: selectedXray.id,
        patient_name: selectedXray.patient_display,
        xray_type: selectedXray.xray_type_display,
      });
    }
  };

  const handleFileChange = (event) => {
    setFormData({ ...formData, image: event.target.files[0] });
  };

  const validateFields = () => {
    const newErrors = {};

    if (!formData.patient_xray) {
      newErrors.patient_xray = t("x_ray.patientXrayRequired");
    }

    if (!formData.findings.trim()) {
      newErrors.findings = t("x_ray.findingsRequired");
    }

    if (formData.image && !formData.image.type.startsWith("image/")) {
      newErrors.image = t("x_ray.invalidImage");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFields()) {
      setOpen(true);
      return;
    }

    const formDataToSubmit = new FormData();
    const allowedFields = ["patient_xray", "image", "findings", "result_date"];

    allowedFields.forEach((key) => {
      if (key === "image" && !formData[key]) {
        return;
      }
      formDataToSubmit.append(key, formData[key]);
    });

    handleAdd(formDataToSubmit);
  };

  const handleClose = () => setOpen(false);

  return (
    <Grid container maxWidth={"xl"} mt={2} justifyContent={"center"} mb={10}>
      <Grid item xs={12} lg={8}>
        <Paper sx={{ boxShadow: "0px 0px 0px ", padding: "30px 40px" }}>
          <Typography variant="h5" gutterBottom>
            {t("x_ray.resultEntry")}
          </Typography>

          <Grid container spacing={4} mt={2}>
            <Grid item xs={12}>
              <FormLabel>{t("x_ray.searchTicket") || "Search ticket"}</FormLabel>
              <TicketSearch
                value={selectedTicket}
                onChange={setSelectedTicket}
                placeholder={t("x_ray.searchByTicket") || "Search by ticket number"}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("x_ray.patientXray")}</FormLabel>
              <Autocomplete
                options={xrayData ?? []}
                getOptionLabel={(option) =>
                  `${option.patient_display} - ${option.xray_type_display}`
                }
                onChange={handlePatientSelect}
                disabled={!selectedTicket}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="patient_xray"
                    value={formData.patient_name || ""}
                    fullWidth
                    size="small"
                    placeholder={
                      selectedTicket
                        ? t("x_ray.selectPatientXray")
                        : t("x_ray.selectTicketFirst") || "Select a ticket first"
                    }
                    error={!!errors.patient_xray}
                    helperText={errors.patient_xray}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("x_ray.xrayType")}</FormLabel>
              <TextField
                name="xray_type"
                value={formData.xray_type || ""}
                fullWidth
                size="small"
                disabled
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <FormLabel>{t("x_ray.findings")}</FormLabel>
              <TextField
                name="findings"
                value={formData.findings}
                onChange={handleInputChange}
                fullWidth
                size="small"
                multiline
                rows={4}
                placeholder={t("x_ray.enterFindings")}
                error={!!errors.findings}
                helperText={errors.findings}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("x_ray.uploadFile")}</FormLabel>
              <TextField
                type="file"
                inputProps={{ accept: "image/*" }}
                onChange={handleFileChange}
                fullWidth
                size="small"
                error={!!errors.image}
                helperText={errors.image}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("x_ray.resultDate")}</FormLabel>
              <TextField
                type="date"
                name="result_date"
                onChange={handleInputChange}
                fullWidth
                size="small"
                defaultValue={new Date().toISOString().split("T")[0]}
              />
            </Grid>
          </Grid>

          <Grid container justifyContent={"flex-end"} mt={4}>
            <Button
              variant="contained"
              style={{ ...buttonStyle, marginLeft: "20px" }}
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading && <CircularProgress size={24} />}
            >
              {loading ? t("x_ray.loading") : t("x_ray.save")}
            </Button>
          </Grid>

          <Snackbar open={open} autoHideDuration={4000} onClose={handleClose}>
            <Alert
              onClose={handleClose}
              severity="error"
              sx={{ width: "100%" }}
            >
              {t("x_ray.errorMessage")}
            </Alert>
          </Snackbar>

          {/* Divider between the form and the list */}
          <Divider sx={{ marginY: 4 }} />

          {/* X-ray Results List */}
          <ListXrayResult />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TestXrayResult;
